// client/src/pages/AdminAnalytics.js
import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, Grid, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import { formatCurrency } from '../utils/currency';
import MiniBarChart from '../components/MiniBarChart';
import { API_BASE_URL } from '../utils/media';

const StatCard = ({ label, value, icon: Icon }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{label}</Typography>
          <Typography variant="h4" sx={{ overflowWrap: 'anywhere' }}>{value}</Typography>
        </Box>
        <Box
          sx={{
            width: 40, height: 40, flexShrink: 0, display: 'grid', placeItems: 'center',
            color: 'primary.main', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
          }}
        >
          <Icon fontSize="small" />
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const AdminAnalytics = () => {
  const { token } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attention, setAttention] = useState(null);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE_URL}/api/admin/analytics`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : null))
      .then(setData)
      .finally(() => setLoading(false));
    fetch(`${API_BASE_URL}/api/admin/reports`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : null))
      .then(setAttention)
      .catch(() => {});
  }, [token]);

  if (loading) return <CircularProgress size={28} />;
  if (!data) return <Typography color="text.secondary">Could not load analytics.</Typography>;

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2.5 }}>Dashboard</Typography>
      {attention?.unpaidOrders.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5 }} action={<Button color="inherit" size="small" component={Link} to="/admin/orders">Review</Button>}>
          {attention.unpaidOrders.length} order{attention.unpaidOrders.length === 1 ? ' is' : 's are'} waiting for EFT payment ({formatCurrency(attention.unpaidTotal)}).
        </Alert>
      )}
      {attention?.lowStock.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5 }} action={<Button color="inherit" size="small" component={Link} to="/admin/reports">Review</Button>}>
          {attention.lowStock.length} product{attention.lowStock.length === 1 ? ' is' : 's are'} low on stock.
        </Alert>
      )}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}><StatCard label="Total revenue (paid orders)" value={formatCurrency(data.revenue)} icon={PaidOutlinedIcon} /></Grid>
        <Grid item xs={6} sm={4}><StatCard label="Paid orders" value={data.orderCount} icon={ShoppingBagOutlinedIcon} /></Grid>
        <Grid item xs={6} sm={4}><StatCard label="Total users" value={data.userCount} icon={PeopleOutlineIcon} /></Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Revenue, last 30 days</Typography>
              <MiniBarChart data={data.revenueByDay} labelKey="day" valueKey="revenue" formatValue={formatCurrency} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>New users, last 30 days</Typography>
              <MiniBarChart data={data.userGrowth} labelKey="day" valueKey="newUsers" />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="subtitle1" fontWeight={700} gutterBottom>Top products</Typography>
      {data.topProducts.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No sales yet.</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Product</TableCell><TableCell align="right">Units sold</TableCell><TableCell align="right">Revenue</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {data.topProducts.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{p.name}</TableCell>
                  <TableCell align="right">{p.unitsSold}</TableCell>
                  <TableCell align="right">{formatCurrency(p.revenue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AdminAnalytics;
